import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usd, fmtDate, StatusPill } from "./affiliateFormat";

const Who = ({ u }) => (
  <div className="min-w-0">
    <p className="text-white text-sm truncate">{u?.name || "—"}</p>
    <p className="text-[11px] text-gray-500 truncate">{u?.email}</p>
  </div>
);

export const AffiliatesTable = ({ rows, onStatus }) => (
  <div className="divide-y divide-white/5" data-testid="admin-affiliates-table">
    {rows.length === 0 && <p className="text-sm text-gray-500 py-4" data-testid="admin-affiliates-empty">No affiliates yet.</p>}
    {rows.map((a) => (
      <div key={a.user_id} className="py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`admin-affiliate-row-${a.user_id}`}>
        <div className="flex-1 flex items-center gap-3"><Who u={a.user} /><StatusPill status={a.status} testId={`admin-affiliate-status-${a.user_id}`} /></div>
        <p className="text-xs text-gray-400 md:w-64">
          {a.signups} signups · {a.balances.paid_conversions} paid · pending {usd(a.balances.pending_cents)} · available {usd(a.balances.available_cents)}
        </p>
        <div className="flex gap-2">
          {a.status !== "approved" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onStatus(a.user_id, "approved")} data-testid={`admin-affiliate-approve-${a.user_id}`}>Approve</Button>}
          {a.status === "pending" && <Button size="sm" variant="outline" onClick={() => onStatus(a.user_id, "rejected")} data-testid={`admin-affiliate-reject-${a.user_id}`}>Reject</Button>}
          {a.status === "approved" && <Button size="sm" variant="outline" onClick={() => onStatus(a.user_id, "suspended")} data-testid={`admin-affiliate-suspend-${a.user_id}`}>Suspend</Button>}
        </div>
      </div>
    ))}
  </div>
);

const CommissionRow = ({ c, onAction }) => {
  const [fee, setFee] = useState("");
  return (
    <div className="py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`admin-commission-row-${c.commission_id}`}>
      <div className="flex-1 grid grid-cols-2 gap-3"><Who u={c.affiliate} /><Who u={c.referred} /></div>
      <p className="text-xs text-gray-400 lg:w-72">
        {c.plan_id} · paid {usd(c.gross_cents)} − tax {usd(c.tax_cents)} − fee {c.fee_cents == null ? "?" : usd(c.fee_cents)}
        {c.refunded_cents ? ` − refund ${usd(c.refunded_cents)}` : ""} = {usd(c.net_cents)} · {fmtDate(c.paid_at)}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-white text-sm" data-testid={`admin-commission-amount-${c.commission_id}`}>{usd(c.amount_cents)}</span>
        <StatusPill status={c.status} testId={`admin-commission-status-${c.commission_id}`} />
        {(c.flags || []).map((f) => <span key={f} className="text-[10px] text-orange-300">{f}</span>)}
        {c.status === "needs_review" && (
          <>
            <Input value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Fee ¢" className="w-20 h-8 text-xs" data-testid={`admin-commission-fee-input-${c.commission_id}`} />
            <Button size="sm" onClick={() => onAction(c.commission_id, { action: "resolve_review", fee_cents: fee === "" ? undefined : Number(fee), dispute_resolved: (c.flags || []).includes("disputed") ? window.confirm("Confirm the Stripe dispute was resolved in RGM's favour?") : undefined })} data-testid={`admin-commission-resolve-${c.commission_id}`}>Resolve</Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={() => onAction(c.commission_id, { action: "reverify" })} data-testid={`admin-commission-reverify-${c.commission_id}`}>Re-check Stripe</Button>
        {!["void", "reversed"].includes(c.status) && <Button size="sm" variant="outline" className="text-rose-300" onClick={() => onAction(c.commission_id, { action: "void", reason: "admin" })} data-testid={`admin-commission-void-${c.commission_id}`}>Void</Button>}
      </div>
    </div>
  );
};

export const CommissionsTable = ({ rows, onAction }) => (
  <div className="divide-y divide-white/5" data-testid="admin-commissions-table">
    {rows.length === 0 && <p className="text-sm text-gray-500 py-4" data-testid="admin-commissions-empty">No commissions yet.</p>}
    {rows.map((c) => <CommissionRow key={c.commission_id} c={c} onAction={onAction} />)}
  </div>
);

export const LegacyReview = ({ data }) => (
  <div data-testid="admin-legacy-review">
    {!data ? <p className="text-sm text-gray-500 py-4">Loading…</p> : (
      <>
        <p className="text-sm text-gray-300 mb-3" data-testid="admin-legacy-summary">
          Referrals saved before June 2, 2026 (no recorded source): <b>{data.total}</b> · pass verification <b className="text-emerald-300">{data.passing}</b> · fail <b className="text-rose-300">{data.failing}</b> · passing & never purchased a membership <b>{data.passing_never_purchased}</b>
        </p>
        <div className="divide-y divide-white/5">
          {data.rows.map((r) => (
            <div key={r.referred_user_id} className="py-2 flex flex-col md:flex-row md:items-center gap-2 text-sm" data-testid={`admin-legacy-row-${r.referred_user_id}`}>
              <div className="flex-1 grid grid-cols-2 gap-3"><Who u={r.referred} /><Who u={r.affiliate} /></div>
              <span className="text-[11px] text-gray-500">{fmtDate(r.joined_at)}{r.has_paid_membership ? " · already purchased" : ""}</span>
              <StatusPill status={r.passes ? "approved" : "rejected"} testId={`admin-legacy-status-${r.referred_user_id}`} />
              {!r.passes && <span className="text-[11px] text-orange-200">{r.failures.join(", ").replaceAll("_", " ")}</span>}
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

export const SkipsTable = ({ rows }) => (
  <div className="divide-y divide-white/5" data-testid="admin-skips-table">
    {rows.length === 0 && <p className="text-sm text-gray-500 py-4">No excluded payments.</p>}
    {rows.map((s) => (
      <div key={s.session_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`admin-skip-row-${s.session_id}`}>
        <div className="flex-1 grid grid-cols-2 gap-3"><Who u={s.affiliate} /><Who u={s.referred} /></div>
        <span className="text-xs text-orange-200">{s.reason.replaceAll("_", " ")}</span>
        <span className="text-[11px] text-gray-500">{fmtDate(s.created_at)}</span>
      </div>
    ))}
  </div>
);
